import type {
  CandidateRow,
  ElementOriginMapping,
  ImportRowDraft,
  MappingReferenceType,
  ProjectElementImport,
  ResourceCodeMappingEntry,
} from '@shared/models/element-import.model';
import type { Baseline, IElement } from '@shared/models/project.model';
import type { IProduct } from '@shared/models/product.model';
import type { Recipe } from '@shared/models/recipe.model';
import type { ConversionFactorUnit } from '@shared/models/unit.model';
import {
  elementQuantityUnitRecord,
  type ElementQuantityExpressionName,
} from '@shared/models/element-quantities.model';
import {
  ProjectChangeType,
  type ProjectChange,
} from '@shared/helpers/pipeline/update-engine.models';
import {
  candidateQuantityFrom,
  candidateUnitFrom,
  tryMaterializeCandidateRow,
} from '@shared/helpers/materialize-candidate.helpers';
import {
  applyCandidateIfcMeasureOverride,
  listAvailableIfcMeasures,
  rowWithActiveIfcMeasure,
  type IfcMeasure,
} from '@shared/helpers/ifc-quantity.helpers';
import {
  buildImportRowDraftsChange,
  draftToOriginMapping,
  getImportRowDraft,
  type ImportRowDraftWrite,
} from '@shared/helpers/import-row-draft.helpers';
import {
  buildReimportChanges,
  mergeReimportedElement,
} from '@shared/helpers/csv-import-reimport.helpers';
import { importReaddParentId } from '@shared/helpers/import-replace.helpers';
import { getSelectedElementQuantityProperty } from '@shared/helpers/element-quantity-core.helpers';
import { isElementQuantityExpressionName } from '@shared/helpers/element-quantity.helpers';
import {
  resolveCandidateProduct,
  resolveCandidateRecipe,
  type ImportedElementsByRowId,
  type ProductsById,
  type RecipesById,
} from './candidate-status.helpers';
import type { MappingsByCode } from './element-imports.store';
import { getSelectableUnitsInConversionFactors } from '@shared/helpers/unit.helpers';
import {
  buildSwitchMappingOverlay,
  isMappingCalibrationCompatible,
  deriveQuantityMappingEntry,
  deriveTypedTotalMappingEntry,
  getImportQuantityOverride,
  getProductDefaultQuantityName,
  getSelectableQuantityNames,
  getSelectableQuantityNamesForRecipe,
  importRowDraftPatchForIfcMeasure,
} from './in-row-mapping.helpers';

// ---------------------------------------------------------------------------
// DEV-3456: bulk fan-out. Editing one row of a multi-selection applies the same
// edit to every selected row. Rows where the edit is impossible are skipped
// silently and stay selected.
//
// Every builder returns ONE batch: all element changes plus a single
// `VersionImports` change (see buildImportRowDraftsChange). That is required
// for correctness, not tidiness — the snackbar's Undo is the global undo, so a
// bulk edit has to be one undo frame, and per-row `VersionImports` changes
// would clobber each other anyway.
// ---------------------------------------------------------------------------

export type BulkRowLookups = {
  version: Baseline;
  mappingsByCode: MappingsByCode;
  productsById: ProductsById;
  recipesById: RecipesById;
  importsById: Record<string, ProjectElementImport | undefined>;
  /** Materialized elements by candidate row id. */
  importedElements: ImportedElementsByRowId;
};

export type BulkRowActionResult = {
  changes: ProjectChange[];
  appliedRowIds: string[];
  skippedRowIds: string[];
};

const EMPTY_RESULT: BulkRowActionResult = {
  changes: [],
  appliedRowIds: [],
  skippedRowIds: [],
};

/** What one row contributes to the batch; `null` means the row is skipped. */
type RowOutcome = {
  elementChanges: ProjectChange[];
  draftWrite?: ImportRowDraftWrite;
} | null;

/** Everything a row's edit needs, resolved the same way the in-row editors do. */
type RowContext = {
  row: CandidateRow;
  /** Parse-time row with the draft IFC measure applied to Quantity/Unit. */
  effectiveRow: CandidateRow;
  /** Set once the row has materialized; drives the switch/reimport paths. */
  element: IElement | undefined;
  unitFrom: string;
  draft: ImportRowDraft;
  mapping: ElementOriginMapping | undefined;
  product: IProduct | undefined;
  recipe: Recipe | undefined;
  referenceType: MappingReferenceType;
};

const resolveRowContext = (
  row: CandidateRow,
  lookups: BulkRowLookups,
): RowContext | undefined => {
  const { version, mappingsByCode, productsById, recipesById } = lookups;
  const element = lookups.importedElements.get(row.id);
  const draft = getImportRowDraft(version, row.id);
  const effectiveRow = rowWithActiveIfcMeasure(row, draft.ifcMeasure);
  const unitFrom = candidateUnitFrom(effectiveRow);
  if (!unitFrom) return undefined;

  // A materialized row carries its mapping on the element's origin (falling
  // back to the DB mapping for its resource code); an incomplete one has only
  // the per-row draft.
  const origin =
    element?.origin?.kind === 'csvImport' ? element.origin : undefined;
  const overlayMapping =
    origin?.source === 'inline' ? origin.mapping : undefined;
  const mapping = element
    ? (overlayMapping ??
      (row.resourceCode ? mappingsByCode[row.resourceCode] : undefined))
    : draftToOriginMapping(draft, unitFrom);

  const referenceType: MappingReferenceType =
    mapping?.referenceType === 'recipe' ? 'recipe' : 'product';
  const referenceId = mapping?.referenceId;

  const product =
    resolveCandidateProduct(
      effectiveRow,
      mappingsByCode,
      productsById,
      overlayMapping,
    ) ??
    (referenceType === 'product' && referenceId
      ? productsById[referenceId]
      : undefined);
  const recipe =
    resolveCandidateRecipe(
      effectiveRow,
      mappingsByCode,
      recipesById,
      overlayMapping,
    ) ??
    (referenceType === 'recipe' && referenceId
      ? recipesById[referenceId]
      : undefined);

  return {
    row,
    effectiveRow,
    element,
    unitFrom,
    draft,
    mapping,
    product,
    recipe,
    referenceType,
  };
};

/** Materialize an incomplete row against `nextDraft` and build its changes. */
const applyDraft = (
  ctx: RowContext,
  lookups: BulkRowLookups,
  nextDraft: ImportRowDraft,
): RowOutcome => {
  const { row, unitFrom } = ctx;
  const effectiveUnitFrom =
    candidateUnitFrom(
      applyCandidateIfcMeasureOverride(row, nextDraft.ifcMeasure),
    ) ?? unitFrom;
  const overlay = draftToOriginMapping(nextDraft, effectiveUnitFrom);
  const total = nextDraft.totalQuantity;
  const result = overlay
    ? tryMaterializeCandidateRow(row, {
        mappingsByCode: lookups.mappingsByCode,
        productsById: lookups.productsById,
        recipesById: lookups.recipesById,
        importsById: lookups.importsById,
        draftMappingsByRowId: { [row.id]: overlay },
        quantityOverridesByRowId:
          total !== undefined && Number.isFinite(total)
            ? { [row.id]: total }
            : undefined,
        selectedQuantityByRowId: nextDraft.selectedQuantity
          ? { [row.id]: nextDraft.selectedQuantity }
          : undefined,
        ifcMeasureByRowId: nextDraft.ifcMeasure
          ? { [row.id]: nextDraft.ifcMeasure }
          : undefined,
        trigger: 'user',
      })
    : ({ ok: false } as const);

  // Materialized: keep only the IFC measure; mapping now lives on origin.
  if (!result.ok) {
    return {
      elementChanges: [],
      draftWrite: { mode: 'replace', draft: nextDraft },
    };
  }
  return {
    elementChanges: [
      {
        type: ProjectChangeType.ElementAdd,
        elementId: lookups.version.id,
        factory: result.element,
        generateUniqueName: false,
      },
    ],
    draftWrite: {
      mode: 'replace',
      draft: nextDraft.ifcMeasure ? { ifcMeasure: nextDraft.ifcMeasure } : null,
    },
  };
};

/**
 * Re-materialize an already imported row against `overlay` and re-derive its
 * quantity in place. Only valid when the mapping *target* is unchanged (a unit
 * or quantity edit) — a product/recipe switch must replace the element's whole
 * subtree, see {@link applySwitch}.
 */
const applyReimport = (
  ctx: RowContext,
  lookups: BulkRowLookups,
  element: IElement,
  overlay: ElementOriginMapping,
  quantityOverride: number | undefined,
  selectedQuantity: ElementQuantityExpressionName | undefined,
): RowOutcome => {
  const result = tryMaterializeCandidateRow(ctx.row, {
    mappingsByCode: lookups.mappingsByCode,
    productsById: lookups.productsById,
    recipesById: lookups.recipesById,
    importsById: lookups.importsById,
    draftMappingsByRowId: { [ctx.row.id]: overlay },
    quantityOverridesByRowId:
      quantityOverride !== undefined
        ? { [ctx.row.id]: quantityOverride }
        : undefined,
    selectedQuantityByRowId: selectedQuantity
      ? { [ctx.row.id]: selectedQuantity }
      : undefined,
    ifcMeasureByRowId: ctx.draft.ifcMeasure
      ? { [ctx.row.id]: ctx.draft.ifcMeasure }
      : undefined,
    trigger: 'user',
  });
  if (!result.ok) return null;

  const changes = buildReimportChanges({
    existing: element,
    fresh: result.element,
  });
  return changes.length ? { elementChanges: changes } : null;
};

/**
 * Swap an imported row's element for one materialized against a new mapping
 * target. The element is removed and re-added under the same id
 * (`regenerateIds: false`) so the switch keeps its identity and every
 * non-imported edit, while its children follow the new product or recipe.
 */
const applySwitch = (
  ctx: RowContext,
  lookups: BulkRowLookups,
  element: IElement,
  overlay: ElementOriginMapping,
  valueOverride: number | undefined,
): RowOutcome => {
  const result = tryMaterializeCandidateRow(ctx.row, {
    mappingsByCode: lookups.mappingsByCode,
    productsById: lookups.productsById,
    recipesById: lookups.recipesById,
    importsById: lookups.importsById,
    draftMappingsByRowId: { [ctx.row.id]: overlay },
    quantityOverridesByRowId:
      valueOverride !== undefined ? { [ctx.row.id]: valueOverride } : undefined,
    ifcMeasureByRowId: ctx.draft.ifcMeasure
      ? { [ctx.row.id]: ctx.draft.ifcMeasure }
      : undefined,
    trigger: 'user',
  });
  // Failing to materialize is a normal outcome here, not a reason to skip: a
  // recipe row carries no per-unit conversion factor, so switching it to a
  // product leaves nothing to convert the CSV Mängd with. Mirror the single-row
  // switch — drop the element and seed the draft, so the row falls back to
  // INCOMPLETE showing the new target and awaiting a rate.
  if (!result.ok) {
    const entry = overlay.mappings[ctx.unitFrom];
    return {
      elementChanges: [
        { type: ProjectChangeType.ElementRemove, elementId: element.id },
      ],
      draftWrite: {
        mode: 'replace',
        draft: {
          referenceId: overlay.referenceId,
          referenceType: overlay.referenceType,
          ifcMeasure: ctx.draft.ifcMeasure,
          ...(valueOverride !== undefined
            ? {
                totalQuantity: valueOverride,
                unitTo: entry?.unitTo,
                quantityFactor: entry?.quantityFactor,
              }
            : {}),
        },
      },
    };
  }

  return {
    elementChanges: [
      { type: ProjectChangeType.ElementRemove, elementId: element.id },
      {
        type: ProjectChangeType.ElementAdd,
        elementId: importReaddParentId(lookups.version, element.id),
        factory: mergeReimportedElement(element, result.element),
        generateUniqueName: false,
        regenerateIds: false,
      },
    ],
    // The mapping now lives on the element's origin; drop any stale draft.
    draftWrite: { mode: 'replace', draft: null },
  };
};

/** Fold per-row outcomes into one batch with a single VersionImports change. */
const collectOutcomes = (
  rows: readonly CandidateRow[],
  lookups: BulkRowLookups,
  buildOutcome: (ctx: RowContext) => RowOutcome,
): BulkRowActionResult => {
  const changes: ProjectChange[] = [];
  const draftWrites: Record<string, ImportRowDraftWrite> = {};
  const appliedRowIds: string[] = [];
  const skippedRowIds: string[] = [];

  for (const row of rows) {
    const ctx = resolveRowContext(row, lookups);
    const outcome = ctx ? buildOutcome(ctx) : null;
    if (!outcome) {
      skippedRowIds.push(row.id);
      continue;
    }
    changes.push(...outcome.elementChanges);
    if (outcome.draftWrite) draftWrites[row.id] = outcome.draftWrite;
    appliedRowIds.push(row.id);
  }

  const draftChange = buildImportRowDraftsChange(lookups.version, draftWrites);
  if (draftChange) changes.push(draftChange);

  return { changes, appliedRowIds, skippedRowIds };
};

/**
 * Map every selected row to the same product or recipe. Imported rows switch
 * their mapping in place (keeping element identity and user edits); incomplete
 * ones get the reference on their draft and materialize when that suffices.
 */
export const buildBulkMappingChanges = (
  rows: readonly CandidateRow[],
  lookups: BulkRowLookups,
  referenceId: string,
  referenceType: MappingReferenceType,
): BulkRowActionResult =>
  collectOutcomes(rows, lookups, (ctx) => {
    // Mirrors the in-row picker: keep the calibration when the new target can
    // still address the quantity it was calibrated for, drop it otherwise.
    // Read it off `ctx.mapping`, never the draft: a materialized row carries its
    // calibration on the element's origin and has no draft at all, so a draft
    // lookup would read "uncalibrated" and carry an incompatible entry across.
    const keepsCalibration = isMappingCalibrationCompatible({
      entry: ctx.mapping?.mappings[ctx.unitFrom],
      referenceType,
      product:
        referenceType === 'product'
          ? lookups.productsById[referenceId]
          : undefined,
      recipe:
        referenceType === 'recipe'
          ? lookups.recipesById[referenceId]
          : undefined,
    });

    if (!ctx.element) {
      const nextDraft: ImportRowDraft = {
        ...ctx.draft,
        referenceId,
        referenceType,
        ...(keepsCalibration
          ? {}
          : {
              // Same as the in-row picker: settle the quantity at the pick so
              // the unit cannot move once the user starts typing (DEV-3575).
              selectedQuantity:
                referenceType === 'product'
                  ? getProductDefaultQuantityName(
                      lookups.productsById[referenceId],
                      ctx.effectiveRow,
                    )
                  : undefined,
              quantityFactor: undefined,
              unitTo: undefined,
              totalQuantity: undefined,
              expression: undefined,
            }),
      };
      return applyDraft(ctx, lookups, nextDraft);
    }

    // Preserve a user-typed quantity across the switch even when the new
    // product's unit differs (DEV-3439).
    const retainedQuantity = getImportQuantityOverride(ctx.element);
    const { mapping, valueOverride } = buildSwitchMappingOverlay({
      row: ctx.effectiveRow,
      referenceId,
      referenceType,
      product:
        referenceType === 'product'
          ? lookups.productsById[referenceId]
          : undefined,
      retainedQuantity,
      carriedEntry: keepsCalibration
        ? ctx.mapping?.mappings[ctx.unitFrom]
        : undefined,
    });

    return applySwitch(ctx, lookups, ctx.element, mapping, valueOverride);
  });

/** The element quantities a row can actually be pointed at. */
const selectableQuantityNames = (
  ctx: RowContext,
): ElementQuantityExpressionName[] =>
  ctx.referenceType === 'recipe'
    ? getSelectableQuantityNamesForRecipe(ctx.recipe)
    : getSelectableQuantityNames(ctx.product);

/** The mapping entry for the picked quantity, or `null` when unsupported. */
const quantityEntry = (
  ctx: RowContext,
  name: ElementQuantityExpressionName,
  typedTotal: number | undefined,
): ResourceCodeMappingEntry | null => {
  if (ctx.referenceType === 'recipe') {
    // Recipes carry no conversion factors: the CSV Mängd goes straight into the
    // picked quantity.
    const qtyFrom = candidateQuantityFrom(ctx.effectiveRow);
    return {
      quantityFactor:
        typedTotal !== undefined && qtyFrom ? typedTotal / qtyFrom : 1,
      quantityName: name,
      unitTo: elementQuantityUnitRecord[name] as
        ConversionFactorUnit | undefined,
    };
  }
  return deriveQuantityMappingEntry({
    row: ctx.effectiveRow,
    product: ctx.product,
    quantityName: name,
    typedTotal,
    // Re-express a prior CSV->nodon factor in the picked unit (st->kg becomes
    // st->m²) so switching quantity keeps the calibrated ratio.
    fromEntry: ctx.mapping?.mappings[ctx.unitFrom],
  });
};

const overlayWithEntry = (
  ctx: RowContext,
  referenceId: string,
  entry: ResourceCodeMappingEntry,
): ElementOriginMapping => ({
  referenceId,
  referenceType: ctx.referenceType,
  mappings: { ...(ctx.mapping?.mappings ?? {}), [ctx.unitFrom]: entry },
});

/**
 * Point every selected row at the same element quantity. This is the ticket's
 * skip case: a row whose product cannot represent the picked quantity (kg on an
 * energy product) is left untouched.
 */
export const buildBulkQuantityNameChanges = (
  rows: readonly CandidateRow[],
  lookups: BulkRowLookups,
  name: ElementQuantityExpressionName,
): BulkRowActionResult =>
  collectOutcomes(rows, lookups, (ctx) => {
    if (!selectableQuantityNames(ctx).includes(name)) return null;

    const referenceId =
      ctx.mapping?.referenceId ??
      (ctx.referenceType === 'recipe' ? ctx.recipe?.id : ctx.product?.id);
    if (!referenceId) return null;

    const entry = quantityEntry(ctx, name, ctx.draft.totalQuantity);
    if (!entry) return null;

    if (ctx.element) {
      return applyReimport(
        ctx,
        lookups,
        ctx.element,
        overlayWithEntry(ctx, referenceId, entry),
        undefined,
        name,
      );
    }

    return applyDraft(ctx, lookups, {
      ...ctx.draft,
      referenceId,
      referenceType: ctx.referenceType,
      selectedQuantity: name,
      unitTo: entry.unitTo,
      quantityFactor: entry.quantityFactor,
    });
  });

/**
 * Apply a typed total across the selection. The number itself is only the
 * *edited* row's value — copying it verbatim onto the others would claim every
 * row happens to measure the same amount. What actually generalises is the rate
 * it implies (`value / edited row's Mängd`), so every other row derives its own
 * value from its own Mängd (DEV-3456).
 *
 * On the edited row a typed total still recalibrates its factor, so total and
 * factor never decouple (DEV-3501).
 */
export const buildBulkTotalQuantityChanges = (
  rows: readonly CandidateRow[],
  lookups: BulkRowLookups,
  value: number,
  /** The row the user typed into; the rest follow its rate. */
  sourceRowId: string,
): BulkRowActionResult => {
  if (!Number.isFinite(value)) return EMPTY_RESULT;

  const sourceRow = rows.find((row) => row.id === sourceRowId);
  const sourceQtyFrom = sourceRow
    ? candidateQuantityFrom(
        rowWithActiveIfcMeasure(
          sourceRow,
          getImportRowDraft(lookups.version, sourceRowId).ifcMeasure,
        ),
      )
    : undefined;
  // Without a Mängd on the edited row there is no rate to generalise; only that
  // row can take the value.
  const spreadFactor =
    sourceQtyFrom !== undefined && sourceQtyFrom !== 0
      ? value / sourceQtyFrom
      : undefined;

  return collectOutcomes(rows, lookups, (ctx) => {
    if (ctx.row.id !== sourceRowId) {
      return spreadFactor === undefined
        ? null
        : conversionOutcome(ctx, lookups, { quantityFactor: spreadFactor });
    }

    const referenceId =
      ctx.mapping?.referenceId ??
      (ctx.referenceType === 'recipe' ? ctx.recipe?.id : ctx.product?.id);
    if (!referenceId) return null;

    if (ctx.element) {
      // The typed total stays in the quantity the element already uses.
      const current = getSelectedElementQuantityProperty(ctx.element)?.name;
      if (!current || !isElementQuantityExpressionName(current)) return null;
      const entry = quantityEntry(ctx, current, value);
      if (!entry) return null;
      return applyReimport(
        ctx,
        lookups,
        ctx.element,
        overlayWithEntry(ctx, referenceId, entry),
        value,
        current,
      );
    }

    if (ctx.referenceType === 'recipe') {
      // A recipe needs no picked quantity for a total to mean something: the
      // Mängd maps straight in and materialization resolves the quantity from
      // the recipe's category. Requiring one here left a freshly recipe-mapped
      // row (which starts with no pick) unable to take a typed value at all.
      const qtyFrom = candidateQuantityFrom(ctx.effectiveRow);
      return applyDraft(ctx, lookups, {
        ...ctx.draft,
        referenceId,
        referenceType: 'recipe',
        quantityFactor: qtyFrom ? value / qtyFrom : 1,
        totalQuantity: value,
        expression: undefined,
      });
    }

    const entry = deriveTypedTotalMappingEntry({
      row: ctx.effectiveRow,
      product: ctx.product,
      qtyTo: value,
      selectedQuantity: ctx.draft.selectedQuantity,
      fallbackUnitTo: ctx.draft.unitTo ?? '',
    });
    if (!entry) return null;

    return applyDraft(ctx, lookups, {
      ...ctx.draft,
      referenceId,
      referenceType: ctx.referenceType,
      unitTo: entry.unitTo,
      quantityFactor: entry.quantityFactor,
      totalQuantity: value,
      expression: undefined,
    });
  });
};

/**
 * Set the same conversion rate on every selected row: the factor, the target
 * unit, or both. Mirrors the header editor's `persistRate` per row — a row whose
 * product cannot convert to `unitTo` is skipped, which is the same rule as the
 * quantity picker.
 *
 * `quantityFactor` is a *rate* (unitTo per unitFrom), not a total, so the same
 * number is meaningful on every row regardless of its own Mängd.
 */
/** One row's outcome for a conversion-rate write; shared with the total-quantity
 * fan-out, where the typed value on one row becomes a rate for the others. */
const conversionOutcome = (
  ctx: RowContext,
  lookups: BulkRowLookups,
  rate: {
    quantityFactor: number | undefined;
    unitTo?: ConversionFactorUnit;
  },
): RowOutcome => {
  const referenceId =
    ctx.mapping?.referenceId ??
    (ctx.referenceType === 'recipe' ? ctx.recipe?.id : ctx.product?.id);
  if (!referenceId) return null;

  const existing = ctx.mapping?.mappings[ctx.unitFrom];
  // A recipe carries no conversion factors, so any unit is addressable; a
  // product must actually be able to convert to the picked one.
  if (rate.unitTo !== undefined && ctx.referenceType === 'product') {
    const selectable = ctx.product
      ? getSelectableUnitsInConversionFactors(ctx.product.conversion_factors)
      : [];
    if (!selectable.includes(rate.unitTo as (typeof selectable)[number])) {
      return null;
    }
  }

  const unitTo = rate.unitTo ?? existing?.unitTo;
  const entry: ResourceCodeMappingEntry = {
    unitTo,
    quantityFactor: rate.quantityFactor,
    // The rate editor only recalibrates; the picked quantity row must survive.
    quantityName: existing?.quantityName,
  };

  if (ctx.element) {
    const outcome = applyReimport(
      ctx,
      lookups,
      ctx.element,
      overlayWithEntry(ctx, referenceId, entry),
      undefined,
      existing?.quantityName,
    );
    if (outcome) return outcome;
    // No longer materializable (e.g. a cleared rate with no auto-conversion):
    // drop the element so the row falls back to an incomplete candidate driven
    // by the draft, mirroring the single-row rate editor (DEV-3501).
    return {
      elementChanges: [
        { type: ProjectChangeType.ElementRemove, elementId: ctx.element.id },
      ],
      draftWrite: {
        mode: 'replace',
        draft: {
          referenceId,
          referenceType: ctx.referenceType,
          ifcMeasure: ctx.draft.ifcMeasure,
          unitTo,
          quantityFactor: rate.quantityFactor,
        },
      },
    };
  }

  return applyDraft(ctx, lookups, {
    ...ctx.draft,
    referenceId,
    referenceType: ctx.referenceType,
    unitTo,
    quantityFactor: rate.quantityFactor,
    // A rate drives the value off each row's own Mängd, so any pinned number
    // override has to go — otherwise the row keeps showing the old total.
    totalQuantity: undefined,
    expression:
      rate.quantityFactor !== undefined &&
      candidateQuantityFrom(ctx.effectiveRow) !== undefined
        ? `${rate.quantityFactor} * ${candidateQuantityFrom(ctx.effectiveRow)}`
        : undefined,
  });
};

/**
 * Set the same conversion rate on every selected row: the factor, the target
 * unit, or both. Mirrors the header editor's `persistRate` per row — a row whose
 * product cannot convert to `unitTo` is skipped, which is the same rule as the
 * quantity picker.
 *
 * `quantityFactor` is a *rate* (unitTo per unitFrom), not a total, so the same
 * number is meaningful on every row regardless of its own Mängd.
 */
export const buildBulkConversionChanges = (
  rows: readonly CandidateRow[],
  lookups: BulkRowLookups,
  rate: {
    quantityFactor: number | undefined;
    /** Omit to keep each row's own target unit and only recalibrate the factor. */
    unitTo?: ConversionFactorUnit;
  },
): BulkRowActionResult =>
  collectOutcomes(rows, lookups, (ctx) =>
    conversionOutcome(ctx, lookups, rate),
  );

/**
 * Point every selected row at the same IFC file measure (Length/Area/Volume/…).
 * Rows that do not expose that measure are skipped.
 */
export const buildBulkIfcMeasureChanges = (
  rows: readonly CandidateRow[],
  lookups: BulkRowLookups,
  measure: IfcMeasure,
): BulkRowActionResult =>
  collectOutcomes(rows, lookups, (ctx) => {
    if (!listAvailableIfcMeasures(ctx.row).includes(measure)) return null;
    const isRecipe = ctx.referenceType === 'recipe';
    const patch = importRowDraftPatchForIfcMeasure(
      ctx.row,
      measure,
      isRecipe ? undefined : ctx.product,
      isRecipe ? ctx.recipe : undefined,
    );
    const nextDraft = { ...ctx.draft, ...patch };
    if (!ctx.element) return applyDraft(ctx, lookups, nextDraft);

    const overlay = ctx.mapping;
    if (!overlay) {
      return {
        elementChanges: [],
        draftWrite: { mode: 'merge', patch },
      };
    }
    const selectedQuantity = nextDraft.selectedQuantity;
    const result = tryMaterializeCandidateRow(ctx.row, {
      mappingsByCode: lookups.mappingsByCode,
      productsById: lookups.productsById,
      recipesById: lookups.recipesById,
      importsById: lookups.importsById,
      draftMappingsByRowId: { [ctx.row.id]: overlay },
      selectedQuantityByRowId: selectedQuantity
        ? { [ctx.row.id]: selectedQuantity }
        : undefined,
      ifcMeasureByRowId: { [ctx.row.id]: measure },
      trigger: 'user',
    });
    if (!result.ok) {
      return {
        elementChanges: [
          {
            type: ProjectChangeType.ElementRemove,
            elementId: ctx.element.id,
          },
        ],
        draftWrite: {
          mode: 'replace',
          draft: {
            referenceId: overlay.referenceId ?? ctx.draft.referenceId,
            referenceType: ctx.referenceType,
            ...patch,
          },
        },
      };
    }
    const elementChanges = buildReimportChanges({
      existing: ctx.element,
      fresh: result.element,
    });
    return {
      elementChanges,
      draftWrite: { mode: 'merge', patch },
    };
  });

/** Snackbar copy for a completed bulk edit (ticket: "<n> rows updated"). */
export const bulkUpdatedMessage = (appliedRowIds: readonly string[]): string =>
  `${appliedRowIds.length} ${appliedRowIds.length === 1 ? 'row' : 'rows'} updated`;
